import { useCallback, useEffect, useState } from "react";
import { Ban, ChevronDown, ChevronUp, Gift, Mail, Plus, RefreshCw, RotateCcw, Save, Search, Ticket, Trash2, Undo2 } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const emptySummary = { paid_orders: 0, tickets_total: 0, tickets_valid: 0, tickets_used: 0, tickets_cancelled: 0, revenue_mxn: 0 };
const statusLabels = { paid: "Pagada", pending: "Pendiente", failed: "Fallida", expired: "Vencida", valid: "Vigente", used: "Utilizado", cancelled: "Cancelado" };

function formatDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" });
}

function Input({ label, value, onChange, type = "text", min, max }) {
  return (
    <label className="block">
      <span className="overline text-[var(--brand-muted)]">{label}</span>
      <input type={type} min={min} max={max} value={value} onChange={(event) => onChange(event.target.value)} className="mt-2 w-full border border-[var(--brand-border)] bg-white px-3 py-2.5 text-sm outline-none focus:border-[var(--brand-primary)]" />
    </label>
  );
}

export default function AdminTickets({ pin }) {
  const [settings, setSettings] = useState({ sales_enabled: true, ticket_types: [] });
  const [summary, setSummary] = useState(emptySummary);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [courtesy, setCourtesy] = useState({ ticket_type_id: "", quantity: 1, buyer_name: "", buyer_email: "", buyer_phone: "" });
  const [courtesyTickets, setCourtesyTickets] = useState([]);
  const [orderSearch, setOrderSearch] = useState("");
  const [orderStatus, setOrderStatus] = useState("all");
  const [expandedOrderId, setExpandedOrderId] = useState("");
  const [busyAction, setBusyAction] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsResponse, summaryResponse, ordersResponse] = await Promise.all([
        api.get("/tickets/settings"),
        api.get("/tickets/admin/summary", { headers: { "x-admin-pin": pin } }),
        api.get("/tickets/admin/orders", { headers: { "x-admin-pin": pin } }),
      ]);
      setSettings(settingsResponse.data);
      setSummary(summaryResponse.data);
      setOrders(ordersResponse.data);
      setCourtesy((current) => ({ ...current, ticket_type_id: current.ticket_type_id || settingsResponse.data.ticket_types[0]?.id || "" }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo cargar el módulo de boletos.");
    } finally {
      setLoading(false);
    }
  }, [pin]);

  useEffect(() => { load(); }, [load]);

  const updateType = (index, key, value) => {
    setSettings((current) => ({
      ...current,
      ticket_types: current.ticket_types.map((item, itemIndex) => itemIndex === index ? { ...item, [key]: value } : item),
    }));
  };

  const addType = () => {
    const suffix = Date.now().toString().slice(-6);
    setSettings((current) => ({
      ...current,
      ticket_types: [...current.ticket_types, { id: `boleto-${suffix}`, name: "Nuevo boleto", description: "", price_mxn: 10, capacity: 100, max_per_order: 6, active: true }],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const response = await api.put("/tickets/settings", {
        sales_enabled: settings.sales_enabled,
        ticket_types: settings.ticket_types.map(({ sold, reserved, available, ...item }) => item),
      }, { headers: { "x-admin-pin": pin } });
      setSettings(response.data);
      toast.success("Configuración de boletos guardada.");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo guardar.");
    } finally {
      setSaving(false);
    }
  };

  const createCourtesy = async (event) => {
    event.preventDefault();
    try {
      const response = await api.post("/tickets/admin/complimentary", { ...courtesy, quantity: Number(courtesy.quantity) }, { headers: { "x-admin-pin": pin } });
      setCourtesyTickets(response.data.tickets);
      toast.success("Cortesía generada. Ya puedes probar el escáner.");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo generar la cortesía.");
    }
  };

  const updateTicket = async (ticketItem, action) => {
    const prompts = {
      cancel: `¿Cancelar el QR ${ticketItem.code}? Dejará de funcionar inmediatamente.`,
      reactivate: `¿Reactivar el QR ${ticketItem.code}? Volverá a permitir un acceso.`,
      reset_checkin: `¿Restablecer ${ticketItem.code}? Podrá utilizarse nuevamente.`,
    };
    if (!window.confirm(prompts[action])) return;
    const key = `${ticketItem.id}:${action}`;
    setBusyAction(key);
    try {
      await api.patch(`/tickets/admin/tickets/${ticketItem.id}`, { action, note: "Acción desde panel de administración" }, { headers: { "x-admin-pin": pin } });
      toast.success(action === "cancel" ? "QR cancelado." : action === "reactivate" ? "QR reactivado." : "Acceso restablecido.");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo actualizar el boleto.");
    } finally {
      setBusyAction("");
    }
  };

  const resendEmail = async (order) => {
    if (!window.confirm(`¿Reenviar todos los boletos a ${order.buyer_email}?`)) return;
    const key = `${order.id}:email`;
    setBusyAction(key);
    try {
      await api.post(`/tickets/admin/orders/${order.id}/resend-email`, {}, { headers: { "x-admin-pin": pin } });
      toast.success(`Boletos reenviados a ${order.buyer_email}.`);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo reenviar el correo.");
    } finally {
      setBusyAction("");
    }
  };

  const normalizedSearch = orderSearch.trim().toLowerCase();
  const filteredOrders = orders.filter((order) => {
    if (orderStatus !== "all" && order.status !== orderStatus) return false;
    if (!normalizedSearch) return true;
    return [order.id, order.buyer_name, order.buyer_email, order.buyer_phone, order.stripe_session_id, ...(order.tickets || []).map((item) => item.code)]
      .some((value) => String(value || "").toLowerCase().includes(normalizedSearch));
  });

  if (loading) return <div className="card-cg p-6 text-sm text-[var(--brand-muted)]">Cargando boletera...</div>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        {[
          ["Órdenes pagadas", summary.paid_orders],
          ["Boletos emitidos", summary.tickets_total],
          ["Disponibles", summary.tickets_valid],
          ["Ingresos", `$${summary.revenue_mxn.toFixed(2)}`],
          ["Accesos", summary.tickets_used],
          ["Cancelados", summary.tickets_cancelled],
        ].map(([label, value]) => (
          <div key={label} className="card-cg bg-white p-4">
            <div className="overline text-[var(--brand-muted)]">{label}</div>
            <div className="font-serif-cg text-3xl mt-2">{value}</div>
          </div>
        ))}
      </div>

      <section className="card-cg bg-white p-5 md:p-6">
        {!settings.stripe_configured || !settings.stripe_webhook_configured || !settings.ticket_security_configured ? (
          <div className="mb-5 border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
            Configuración pendiente en Render: {!settings.stripe_configured ? "STRIPE_SECRET_KEY " : ""}{!settings.stripe_webhook_configured ? "STRIPE_WEBHOOK_SECRET " : ""}{!settings.ticket_security_configured ? "TICKET_SIGNING_SECRET" : ""}.
          </div>
        ) : null}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-serif-cg text-2xl">Venta e inventario</h2>
            <p className="text-sm text-[var(--brand-muted)] mt-1">Al precio configurado se agrega automáticamente un cargo por servicio de ${(settings.service_fee_mxn ?? 40).toFixed(2)} por boleto.</p>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={addType} className="btn-secondary-cg"><Plus size={15} /> Tipo</button>
            <button type="button" onClick={save} disabled={saving} className="btn-primary-cg"><Save size={15} /> {saving ? "Guardando..." : "Guardar"}</button>
          </div>
        </div>

        <label className="flex items-center gap-3 mt-6 border border-[var(--brand-border)] p-3">
          <input type="checkbox" checked={settings.sales_enabled} onChange={(event) => setSettings({ ...settings, sales_enabled: event.target.checked })} />
          <span className="text-sm font-semibold">Venta pública habilitada</span>
        </label>

        <div className="space-y-4 mt-5">
          {settings.ticket_types.map((item, index) => (
            <article key={item.id} className="border border-[var(--brand-border)] p-4 md:p-5">
              <div className="flex items-center justify-between gap-3 mb-4">
                <div>
                  <h3 className="font-serif-cg text-xl">{item.name}</h3>
                  <p className="text-xs text-[var(--brand-muted)] mt-1">{item.sold || 0} vendidos · {item.available ?? item.capacity} disponibles</p>
                </div>
                {settings.ticket_types.length > 1 ? (
                  <button type="button" onClick={() => setSettings((current) => ({ ...current, ticket_types: current.ticket_types.filter((_, i) => i !== index) }))} className="h-9 w-9 inline-flex items-center justify-center text-red-700 border border-red-200" aria-label="Eliminar tipo"><Trash2 size={16} /></button>
                ) : null}
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Input label="Identificador" value={item.id} onChange={(value) => updateType(index, "id", value)} />
                <Input label="Nombre" value={item.name} onChange={(value) => updateType(index, "name", value)} />
                <Input label="Precio MXN" type="number" min="10" value={item.price_mxn} onChange={(value) => updateType(index, "price_mxn", Number(value))} />
                <Input label="Capacidad" type="number" min="1" value={item.capacity} onChange={(value) => updateType(index, "capacity", Number(value))} />
                <Input label="Máximo por compra" type="number" min="1" max="10" value={item.max_per_order} onChange={(value) => updateType(index, "max_per_order", Number(value))} />
                <label className="flex items-center gap-3 self-end border border-[var(--brand-border)] px-3 py-2.5">
                  <input type="checkbox" checked={item.active} onChange={(event) => updateType(index, "active", event.target.checked)} />
                  <span className="text-sm">Boleto activo</span>
                </label>
                <label className="md:col-span-2 lg:col-span-3 block">
                  <span className="overline text-[var(--brand-muted)]">Descripción</span>
                  <textarea value={item.description} onChange={(event) => updateType(index, "description", event.target.value)} className="mt-2 w-full min-h-20 border border-[var(--brand-border)] px-3 py-2.5 text-sm outline-none focus:border-[var(--brand-primary)]" />
                </label>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="card-cg bg-white p-5 md:p-6">
        <div className="flex items-center gap-3">
          <Gift className="text-[var(--brand-primary)]" size={22} />
          <div>
            <h2 className="font-serif-cg text-2xl">Cortesía de prueba</h2>
            <p className="text-sm text-[var(--brand-muted)]">Genera un QR sin cobrar para probar el escáner del staff.</p>
          </div>
        </div>
        <form onSubmit={createCourtesy} className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 mt-5">
          <label className="block">
            <span className="overline text-[var(--brand-muted)]">Tipo</span>
            <select value={courtesy.ticket_type_id} onChange={(event) => setCourtesy({ ...courtesy, ticket_type_id: event.target.value })} className="mt-2 w-full border border-[var(--brand-border)] px-3 py-2.5 text-sm">
              {settings.ticket_types.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </label>
          <Input label="Cantidad" type="number" min="1" max="20" value={courtesy.quantity} onChange={(value) => setCourtesy({ ...courtesy, quantity: Number(value) })} />
          <Input label="Nombre" value={courtesy.buyer_name} onChange={(value) => setCourtesy({ ...courtesy, buyer_name: value })} />
          <Input label="Correo" type="email" value={courtesy.buyer_email} onChange={(value) => setCourtesy({ ...courtesy, buyer_email: value })} />
          <button type="submit" className="btn-primary-cg self-end justify-center"><Gift size={15} /> Generar</button>
        </form>

        {courtesyTickets.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {courtesyTickets.map((ticket) => (
              <div key={ticket.id} className="border border-[var(--brand-border)] p-4 text-center">
                <QRCodeCanvas value={ticket.qr_token} size={240} level="M" marginSize={4} />
                <div className="font-mono font-semibold text-sm mt-3">{ticket.code}</div>
                <div className="text-xs text-[var(--brand-muted)] mt-1">{ticket.buyer_name}</div>
              </div>
            ))}
          </div>
        ) : null}
      </section>

      <section className="card-cg bg-white p-5 md:p-6 overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div><h2 className="font-serif-cg text-2xl flex items-center gap-2"><Ticket size={20} /> Administración de boletos</h2><p className="text-sm text-[var(--brand-muted)] mt-1">Consulta compradores, pagos, folios y estados de acceso.</p></div>
          <button type="button" onClick={load} className="btn-secondary-cg"><RefreshCw size={14} /> Actualizar</button>
        </div>
        <div className="grid md:grid-cols-[1fr_190px] gap-3 mt-5">
          <label className="relative"><Search size={16} className="absolute left-3 top-3 text-[var(--brand-muted)]" /><input value={orderSearch} onChange={(event) => setOrderSearch(event.target.value)} placeholder="Nombre, correo, teléfono, orden o folio" className="w-full border border-[var(--brand-border)] py-2.5 pl-10 pr-3 text-sm outline-none focus:border-[var(--brand-primary)]" /></label>
          <select value={orderStatus} onChange={(event) => setOrderStatus(event.target.value)} className="border border-[var(--brand-border)] bg-white px-3 py-2.5 text-sm"><option value="all">Todos los estados</option><option value="paid">Pagadas</option><option value="pending">Pendientes</option><option value="failed">Fallidas</option><option value="expired">Vencidas</option></select>
        </div>
        <div className="mt-5 divide-y divide-[var(--brand-border)] border-y border-[var(--brand-border)]">
          {filteredOrders.map((order) => {
            const expanded = expandedOrderId === order.id;
            return (
              <article key={order.id} className="py-4">
                <button type="button" onClick={() => setExpandedOrderId(expanded ? "" : order.id)} className="grid w-full grid-cols-[1fr_auto] items-start gap-4 text-left">
                  <div className="min-w-0"><div className="flex flex-wrap items-center gap-2"><strong>{order.buyer_name}</strong><span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider border ${order.status === "paid" ? "border-green-300 text-green-800" : "border-amber-300 text-amber-800"}`}>{statusLabels[order.status] || order.status}</span><span className="text-[10px] uppercase tracking-wider text-[var(--brand-muted)]">{order.source === "courtesy" ? "Cortesía" : "Stripe"}</span></div><p className="text-xs text-[var(--brand-muted)] mt-1 break-all">{order.buyer_email}{order.buyer_phone ? ` · ${order.buyer_phone}` : ""}</p><p className="text-xs mt-2">{order.quantity} {order.quantity === 1 ? "boleto" : "boletos"} · {order.ticket_type_name} · <strong>${(order.amount_cents / 100).toFixed(2)} MXN</strong></p></div>
                  {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>
                {expanded ? (
                  <div className="mt-5 border-t border-dashed border-[var(--brand-border)] pt-5">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
                      <div><span className="overline text-[var(--brand-muted)]">Orden</span><p className="font-mono break-all mt-1">{order.id}</p></div>
                      <div><span className="overline text-[var(--brand-muted)]">Compra</span><p className="mt-1">{formatDate(order.paid_at || order.created_at)}</p></div>
                      <div><span className="overline text-[var(--brand-muted)]">Correo</span><p className="mt-1">{order.email_status || "pending"}{order.email_resent_at ? ` · reenviado ${formatDate(order.email_resent_at)}` : ""}</p></div>
                      <div><span className="overline text-[var(--brand-muted)]">Pago</span><p className="mt-1">Subtotal ${((order.subtotal_cents || 0) / 100).toFixed(2)} · Servicio ${((order.service_fee_cents || 0) / 100).toFixed(2)}</p></div>
                      {order.discount_code ? <div><span className="overline text-[var(--brand-muted)]">Descuento</span><p className="mt-1">{order.discount_code} · -${((order.discount_cents || 0) / 100).toFixed(2)}</p></div> : null}
                      {order.stripe_session_id ? <div className="sm:col-span-2"><span className="overline text-[var(--brand-muted)]">Sesión Stripe</span><p className="font-mono break-all mt-1">{order.stripe_session_id}</p></div> : null}
                    </div>
                    {order.status === "paid" ? <button type="button" onClick={() => resendEmail(order)} disabled={busyAction === `${order.id}:email`} className="btn-secondary-cg mt-5 disabled:opacity-50"><Mail size={15} /> {busyAction === `${order.id}:email` ? "Enviando..." : "Reenviar boletos"}</button> : null}
                    <div className="mt-5 space-y-3">
                      {(order.tickets || []).map((ticketItem) => (
                        <div key={ticketItem.id} className="grid md:grid-cols-[1fr_auto] gap-4 border border-[var(--brand-border)] p-4">
                          <div className="min-w-0"><div className="flex flex-wrap items-center gap-2"><strong className="font-mono">{ticketItem.code}</strong><span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider border ${ticketItem.status === "valid" ? "border-green-300 text-green-800" : ticketItem.status === "used" ? "border-amber-300 text-amber-800" : "border-red-300 text-red-800"}`}>{statusLabels[ticketItem.status] || ticketItem.status}</span></div><p className="text-xs text-[var(--brand-muted)] mt-1">{ticketItem.buyer_name} · {ticketItem.ticket_type_name}</p>{ticketItem.checked_in_at ? <p className="text-xs mt-2">Acceso: {formatDate(ticketItem.checked_in_at)} por {ticketItem.checked_in_by || "Staff"}</p> : null}{ticketItem.cancelled_at ? <p className="text-xs text-red-700 mt-2">Cancelado: {formatDate(ticketItem.cancelled_at)}</p> : null}</div>
                          <div className="flex flex-wrap md:justify-end gap-2">
                            {ticketItem.status === "valid" ? <button type="button" onClick={() => updateTicket(ticketItem, "cancel")} disabled={busyAction === `${ticketItem.id}:cancel`} className="inline-flex items-center gap-2 border border-red-300 px-3 py-2 text-xs font-semibold text-red-700 disabled:opacity-50"><Ban size={14} /> Cancelar QR</button> : null}
                            {ticketItem.status === "cancelled" ? <button type="button" onClick={() => updateTicket(ticketItem, "reactivate")} disabled={busyAction === `${ticketItem.id}:reactivate`} className="inline-flex items-center gap-2 border border-green-300 px-3 py-2 text-xs font-semibold text-green-800 disabled:opacity-50"><RotateCcw size={14} /> Reactivar</button> : null}
                            {ticketItem.status === "used" ? <><button type="button" onClick={() => updateTicket(ticketItem, "reset_checkin")} disabled={busyAction === `${ticketItem.id}:reset_checkin`} className="inline-flex items-center gap-2 border border-amber-300 px-3 py-2 text-xs font-semibold text-amber-800 disabled:opacity-50"><Undo2 size={14} /> Restablecer</button><button type="button" onClick={() => updateTicket(ticketItem, "cancel")} disabled={busyAction === `${ticketItem.id}:cancel`} className="inline-flex items-center gap-2 border border-red-300 px-3 py-2 text-xs font-semibold text-red-700 disabled:opacity-50"><Ban size={14} /> Cancelar</button></> : null}
                          </div>
                        </div>
                      ))}
                      {!(order.tickets || []).length ? <p className="border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">Esta orden todavía no tiene boletos emitidos.</p> : null}
                    </div>
                    {(order.admin_events || []).length ? <div className="mt-5 border-t border-[var(--brand-border)] pt-4"><span className="overline text-[var(--brand-muted)]">Historial administrativo</span><div className="mt-2 space-y-1 text-xs text-[var(--brand-muted)]">{order.admin_events.map((eventItem) => <p key={eventItem.id}>{formatDate(eventItem.created_at)} · {{ cancel: "QR cancelado", reactivate: "QR reactivado", reset_checkin: "Acceso restablecido", resend_email: "Correo reenviado" }[eventItem.action] || eventItem.action}</p>)}</div></div> : null}
                  </div>
                ) : null}
              </article>
            );
          })}
          {!filteredOrders.length ? <div className="py-10 text-center text-sm text-[var(--brand-muted)]">No encontramos órdenes con esos filtros.</div> : null}
        </div>
      </section>
    </div>
  );
}
